import { useEffect } from 'react';
import { useLicense } from '@/state/license';
import { useStore } from '@/state/store';

/**
 * The two ways an unlock code arrives on its own.
 *
 * `/unlock?ref=…` is the checkout coming back: the reference is whatever the
 * payment provider handed the customer, and it has to be traded with the
 * licence service for a signed code. This is the only request either app ever
 * makes about a purchase.
 *
 * `/unlock#NC1.…` is the phone handing over a code it already has. It carries
 * the whole thing, so nothing is contacted at all — and putting it after the
 * `#` keeps it out of server logs and out of the Referer header on the way.
 *
 * Either way the address bar is tidied afterwards, so a bookmark or a back
 * button does not replay a redemption that already happened.
 */
export function useUnlockLink() {
  useEffect(() => {
    if (window.location.pathname.replace(/\/$/, '') !== '/unlock') return;

    const toast = useStore.getState().toast;
    const { redeem, claim } = useLicense.getState();
    const done = (message: string, kind: 'success' | 'error') => {
      window.history.replaceState(null, '', '/');
      toast(message, kind);
    };

    const fromHash = window.location.hash.slice(1);
    if (fromHash) {
      const ok = redeem(decodeURIComponent(fromHash));
      done(
        ok ? 'Unlocked — this browser is yours now.' : 'That unlock code could not be read.',
        ok ? 'success' : 'error',
      );
      return;
    }

    const reference = new URLSearchParams(window.location.search).get('ref');
    if (!reference) {
      done('There was no purchase reference in that link.', 'error');
      return;
    }

    void claim(reference).then(
      () => done('Unlocked — thank you.', 'success'),
      (error: unknown) =>
        done(error instanceof Error ? error.message : 'That purchase could not be confirmed.', 'error'),
    );
  }, []);
}
